import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GraduationCap, BookOpen, Users, ArrowRight, CheckCircle } from "lucide-react";
import NavBar from "@/components/NavBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function CategoryPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exam-types`);
      const data = await response.json();
      setCategories(data.exam_types || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      // Fallback categories
      setCategories([
        { id: "tgt", label: "TGT (Trained Graduate Teacher)" },
        { id: "pgt", label: "PGT (Post Graduate Teacher)" },
        { id: "prt", label: "PRT (Primary Teacher)" },
        { id: "non_teaching", label: "Non-Teaching Staff" }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case "pgt":
        return <GraduationCap className="w-16 h-16" />;
      case "tgt":
        return <BookOpen className="w-16 h-16" />;
      case "prt":
        return <Users className="w-16 h-16" />;
      default:
        return <BookOpen className="w-16 h-16" />;
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/category/${categoryId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading categories...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Helmet>
        <title>EMRS KVS NVS Exam Categories 2026 | TGT PGT PRT Tier-2 Preparation</title>
        <meta name="description" content="Choose your exam category for EMRS, KVS, NVS Tier-2 preparation. TGT (Trained Graduate Teacher), PGT (Post Graduate Teacher), PRT (Primary Teacher), Non-Teaching staff. Complete syllabus coverage, mock tests, descriptive practice, MCQ question bank." />
        <meta name="keywords" content="EMRS exam categories, KVS TGT PGT PRT, NVS exam preparation, EMRS TGT 2026, EMRS PGT 2026, KVS PRT exam, EMRS teacher recruitment, KVS tier 2 categories, NVS tier 2, EMRS non teaching, TGT exam subjects, PGT exam subjects, PRT exam preparation" />
        <meta property="og:title" content="EMRS KVS NVS Exam Categories | TGT PGT PRT Tier-2" />
        <meta property="og:description" content="Select your exam category - TGT, PGT, PRT, Non-Teaching. Complete preparation for EMRS, KVS, NVS Tier-2 exams." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://descriptivetest.com/category" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "EMRS KVS NVS Exam Categories",
          "description": "Choose your teaching exam category for Tier-2 preparation",
          "url": "https://descriptivetest.com/category",
          "provider": { "@type": "Organization", "name": "Descriptive Test", "url": "https://descriptivetest.com" },
          "hasPart": [
            { "@type": "Course", "name": "TGT (Trained Graduate Teacher)", "url": "https://descriptivetest.com/category/tgt" },
            { "@type": "Course", "name": "PGT (Post Graduate Teacher)", "url": "https://descriptivetest.com/category/pgt" },
            { "@type": "Course", "name": "PRT (Primary Teacher)", "url": "https://descriptivetest.com/category/prt" },
            { "@type": "Course", "name": "Non-Teaching Staff", "url": "https://descriptivetest.com/category/non_teaching" }
          ]
        })}</script>
      </Helmet>
      {/* NavBar with profile */}
      <NavBar />

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-4xl font-bold text-gray-900">
            Select Your Exam Category
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            Choose the teaching exam you're preparing for
          </p>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {categories.map((category) => (
            <div
              key={category.id}
              onClick={() => handleCategoryClick(category.id)}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-blue-500 p-8 group"
            >
              {/* Icon */}
              <div className="flex justify-center mb-6 text-blue-600 group-hover:text-blue-700 transition-colors">
                {getCategoryIcon(category.id)}
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
                {category.label}
              </h2>

              {/* Features */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">Complete Syllabus Coverage</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">KVS & EMRS Exam Patterns</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-600 text-sm">CBSE Practice Tests</span>
                </div>
              </div>

              {/* Button */}
              <div className="flex items-center justify-center gap-2 text-blue-600 group-hover:text-blue-700 font-semibold">
                <span>View Subjects</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Why Choose Our Platform?
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-lg text-gray-800 mb-2">
                Comprehensive Coverage
              </h4>
              <p className="text-gray-600">
                Complete syllabus for all subjects with NCERT notes and CBSE practice questions.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg text-gray-800 mb-2">
                Exam-Specific Preparation
              </h4>
              <p className="text-gray-600">
                Tailored content for KVS, EMRS, NVS, and other teaching recruitment exams.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg text-gray-800 mb-2">
                Instant Evaluation
              </h4>
              <p className="text-gray-600">
                Get instant feedback on descriptive answers with detailed scoring and suggestions.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-lg text-gray-800 mb-2">
                Mock Tests & MCQs
              </h4>
              <p className="text-gray-600">
                Practice with full-length mock tests and unlimited CBSE MCQs for each topic.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
